import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AbsenceTypeFromModel, AbsenceResponse, AbsenceSliceModel, AbsenceRequest, AbsenceFilesRequest, AbsenceFilesResponse, AbsenceNotificationSetupResponse } from "./absences-interfaces";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { OvertimePool } from "features/TimeTracking/state/timeTrackingInterfaces";
import { API_BASE_URL as baseUri } from "config/env";

// get absences
export const GetAbsences = createAsyncThunk<AbsenceResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetAbsences',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/absence/getallabsencerequestsbytenant', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { absenceRequestResponses: AbsenceResponse[] };
            return responseModel.absenceRequestResponses;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// get absence by ID
export const GetAbsenceById = createAsyncThunk<AbsenceResponse, string, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetAbsenceById',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/getmyabsencerequest?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AbsenceResponse;
            return { ...responseModel, variant: responseModel.variant ?? 0 };
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// cancel absence
export const CancelAbsence = createAsyncThunk<AbsenceResponse[], string, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/CancelAbsence',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/cancelabsencerequest?id=' + id, {
            method: "POST",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            dispatch(GetAbsences());
            const responseModel = await response.json() as AbsenceResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetAbsenceTypeDTOs = createAsyncThunk<AbsenceTypeFromModel[], void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetAbsenceTypeDTOs',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/absence-type/get-all/dto', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AbsenceTypeFromModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const UpdateAbsence = createAsyncThunk<AbsenceResponse, AbsenceRequest, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/UpdateAbsence',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/updateabsencerequest', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ ...request, absenceId: request.id })
        });
        if (response.status === 200) {
            dispatch(GetAbsences())
            const responseModel = await response.json() as AbsenceResponse;
            dispatch(showNotification({ message: "Absence updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const DeleteAbsenceFile = createAsyncThunk<AbsenceResponse, string, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/DeleteAbsenceFile',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/deleteabsencefile?absenceFileId=' + request, {
            method: "DELETE",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AbsenceResponse;
            dispatch(showNotification({ message: "Absence file deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const CreateAbsence = createAsyncThunk<AbsenceResponse, AbsenceRequest, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/CreateAbsence',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/absence/createabsencerequest', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAbsences())
            const responseModel = await response.json() as AbsenceResponse;
            dispatch(showNotification({ message: "Absence created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetRemainingVacationDays = createAsyncThunk<{ remainingVacationDays: number }, void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetRemainingVacationDays',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/absence/getremainingvacationdays', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { remainingVacationDays: number };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const GetOvertimePool = createAsyncThunk<OvertimePool, void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetOvertimePool',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/time-tracking/overtime', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as OvertimePool;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const DownloadAbsenceFile = createAsyncThunk<{ fileName: string, fileBase64: string }, string, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/DownloadAbsenceFile',
    async (fileId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/absence/downloadabsencefile?absenceFileId=' + fileId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { fileName: string, fileBase64: string };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const UploadAbsenceFiles = createAsyncThunk<AbsenceFilesResponse, AbsenceFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/UploadAbsenceFiles',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/uploadabsencefile', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAbsenceById(request.absenceId));
            const responseModel = await response.json() as AbsenceFilesResponse;
            dispatch(showNotification({ message: "Absence files uploaded successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const GetMyAbsences = createAsyncThunk<AbsenceResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetMyAbsences',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/absence/getallmyabsencerequests', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { absenceRequestResponses: AbsenceResponse[] };
            return responseModel.absenceRequestResponses;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

/**
 * Feeds the absence form's "notify colleagues" section (WP1): whether it is offered at all, and
 * whom it may offer. Deliberately one call - both answers come from the tenant gate, and asking
 * separately invites a form that renders the section and then empties it.
 * <para>
 * A failure is swallowed rather than shown. The section is optional and the absence form has to
 * stay usable without it; a toast about a notification the user has not asked for yet would be
 * noise on a screen whose job is filing an absence.
 * </para>
 */
export const GetAbsenceNotificationSetup = createAsyncThunk<AbsenceNotificationSetupResponse, void, { rejectValue: RejectedValue }>(
    'Slice/AbsencesSlice/GetAbsenceNotificationSetup',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence/notification-setup', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AbsenceNotificationSetupResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

const initialValue: AbsenceSliceModel = {
    absenceTypes: [],
    absences: [],
    myAbsences: [],
    absence: null,
    notificationSetup: null
}

export const AbsencesSlice = createSlice({
    name: 'AbsencesSlice',
    initialState: initialValue,
    reducers: {
        resetAbsenceSlice: (state) => {
            state.absenceTypes = [];
            state.absences = [];
            state.myAbsences = [];
            state.absence = null;
            state.notificationSetup = null;
        },
        resetAbsence: (state) => {
            state.absence = null;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetAbsences.fulfilled, (state, action: PayloadAction<AbsenceResponse[]>) => {
            state.absences = action.payload;
        })

        builder.addCase(GetMyAbsences.fulfilled, (state, action: PayloadAction<AbsenceResponse[]>) => {
            state.myAbsences = action.payload;
        })

        builder.addCase(GetAbsenceById.fulfilled, (state, action: PayloadAction<AbsenceResponse>) => {
            state.absence = action.payload;
        })

        builder.addCase(GetAbsenceTypeDTOs.fulfilled, (state, action: PayloadAction<AbsenceTypeFromModel[]>) => {
            state.absenceTypes = action.payload;
        })

        builder.addCase(GetAbsenceNotificationSetup.fulfilled, (state, action: PayloadAction<AbsenceNotificationSetupResponse>) => {
            state.notificationSetup = action.payload;
        })

        // A failed load leaves the section hidden rather than half-drawn. Same reasoning as the
        // silent reject in the thunk: the absence form must stay usable without it.
        builder.addCase(GetAbsenceNotificationSetup.rejected, (state) => {
            state.notificationSetup = null;
        })
    }
})

export const { resetAbsence, resetAbsenceSlice } = AbsencesSlice.actions;
export default AbsencesSlice.reducer;